import React, { useState } from 'react';
import { useForm } from '@mantine/form';
import {
    Anchor,
    Button,
    Checkbox,
    Divider,
    Group,
    Stack,
    Text,
    TextInput,
    PasswordInput,
    Popover,
    Progress,
    Box,
    Alert,
    Flex
} from '@mantine/core';
import { IconX, IconCheck } from '@tabler/icons-react';
import { SocialAuth } from './SocialAuth';
import { handleSignUp } from "../../api/Auth";
import { useNavigate, useLocation } from "react-router-dom";
import {useMediaQuery} from "@mantine/hooks";
import logger from '../../utils/logger';

interface SignUpProps {
    onLoginClick: () => void;
}

const requirements = [
    { re: /[0-9]/, label: 'Includes number' },
    { re: /[a-z]/, label: 'Includes lowercase letter' },
    { re: /[A-Z]/, label: 'Includes uppercase letter' },
];

function PasswordRequirement({ meets, label }: { meets: boolean; label: string }) {
    return (
        <Text
            color={meets ? 'teal' : 'red'}
            style={{ display: 'flex', alignItems: 'center' }}
            mt={7}
            size="sm"
        >
            {meets ? <IconCheck style={{ width: 14, height: 14 }} /> : <IconX style={{ width: 14, height: 14 }} />} <Box ml={10}>{label}</Box>
        </Text>
    );
}

function getStrength(password: string) {
    let multiplier = password.length > 7 ? 0 : 1;

    requirements.forEach((requirement) => {
        if (!requirement.re.test(password)) {
            multiplier += 1;
        }
    });

    return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10);
}

const SignUp: React.FC<SignUpProps> = ({ onLoginClick }) => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [submitting, setSubmitting] = useState(false);
    const [popoverOpened, setPopoverOpened] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();

    const form = useForm({
        initialValues: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            confirmPassword: '',
            terms: false,
        },
        validate: {
            firstName: (val) => (val.length ? null : 'First name is required'),
            lastName: (val) => (val.length ? null : 'Last name is required'),
            email: (val) => (/^\S+@\S+$/.test(val) ? null : 'Invalid email'),
            password: (val) => (val.length >= 8 && /[a-z]/.test(val) && /[A-Z]/.test(val) && /[0-9]/.test(val) ? null : 'Password does not meet requirements'),
            confirmPassword: (val, values) => (val === values.password ? null : 'Passwords do not match'),
            terms: (val) => (val ? null : 'You must accept terms and conditions'),
        },
    });

    const checks = requirements.map((requirement, index) => (
        <PasswordRequirement key={index} label={requirement.label} meets={requirement.re.test(form.values.password)} />
    ));

    const strength = getStrength(form.values.password);
    const color = strength === 100 ? 'teal' : strength > 50 ? 'yellow' : 'red';

    const handleSubmit = async (values: { firstName: string; lastName: string; email: string; password: string; confirmPassword: string; terms: boolean }) => {
        try {
            setSubmitting(true);

            const result = await handleSignUp(values.email, values.password, values.firstName, values.lastName);
            
            if (result.success) {
                logger.log(`Sign-up successful. User ID: ${result.sub}`);

                if (!result.isVerified) { // todo: instead of force unpacking, i should verify the params also exist
                    logger.log("Building verification URL");

                    // Build verification URL with all necessary parameters
                    const verifyParams = new URLSearchParams({
                        username: result.sub!,
                        method: result.codeDeliveryDetails?.DeliveryMedium?.toLowerCase()!,
                        destination: result.codeDeliveryDetails?.Destination!
                    });

                    logger.log(verifyParams);

                    // Redirect to verification page with parameters
                    navigate(`/verify-email?${verifyParams.toString()}`);
                } else {
                    // User is already verified, proceed to logged-in state
                    logger.log("User is verified. Proceeding to dashboard.");
                    navigate('/gallery');
                }
                
                setFormSubmitted(true);
                setErrorMessage('');
            } else {
                // Handle specific error cases
                switch (result.errorCode) {
                    case 'UsernameExists':
                        setErrorMessage('An account with this email already exists. Please try signing in.');
                        navigate(`/auth/login?email=${encodeURIComponent(values.email)}&exists=true`);
                        break;
                    case 'InvalidPassword':
                        setErrorMessage('Password does not meet the security requirements. Please choose a stronger password.');
                        break;
                    case 'TooManyRequests':
                        setErrorMessage('Too many attempts. Please try again in a few minutes.');
                        break;
                    default:
                        setErrorMessage('Our site is having trouble registering new users at this time. The engineering team has been notified. Please try again later.');
                        logger.error('Sign-up error:', result.error);
                }
            }
        } catch (error) {
            logger.error('Sign-up error:', error);
            setErrorMessage(error.message || 'An error occurred during sign-up');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Text size="xl" fw={500} mb="md">
                Create an Account
            </Text>
            {/* Social auth is hidden for now */}
            {/* <Flex mb="md" mt="md" gap={isMobile ? 'sm': 'md'} direction={isMobile ? 'column' : 'row'}>
                <SocialAuth
                    onSuccess={(response: any) => {
                        console.log('Apple sign up success:', response);
                    }}
                    provider="Apple"
                    authType="Sign Up"
                />
                <SocialAuth
                    onSuccess={(response: any) => {
                        console.log('Google sign up success:', response);
                    }}
                    provider="Google"
                    authType="Sign Up"
                />
            </Flex>
            <Divider label="Or sign up with email" labelPosition="center" my="lg" /> */}
            {errorMessage && (
                <Alert title="Error" color="red" mb="lg">
                    {errorMessage}
                </Alert>
            )}
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack>

                    {isMobile ? (
                        <>
                            <TextInput
                                label="First Name"
                                withAsterisk
                                placeholder="Your first name"
                                value={form.values.firstName}
                                onChange={(event) => form.setFieldValue('firstName', event.currentTarget.value)}
                                error={form.errors.firstName}
                                radius="md"
                            />
                            <TextInput
                                label="Last Name"
                                withAsterisk
                                placeholder="Your last name"
                                value={form.values.lastName}
                                onChange={(event) => form.setFieldValue('lastName', event.currentTarget.value)}
                                error={form.errors.lastName}
                                radius="md"
                            />
                        </>
                    ) : (
                        <Group grow align={"flex-start"}>
                            <TextInput
                                label="First Name"
                                withAsterisk
                                placeholder="Your first name"
                                value={form.values.firstName}
                                onChange={(event) => form.setFieldValue('firstName', event.currentTarget.value)}
                                error={form.errors.firstName}
                                radius="md"
                            />
                            <TextInput
                                label="Last Name"
                                withAsterisk
                                placeholder="Your last name"
                                value={form.values.lastName}
                                onChange={(event) => form.setFieldValue('lastName', event.currentTarget.value)}
                                error={form.errors.lastName}
                                radius="md"
                            />
                        </Group>
                    )}
                    <TextInput
                        label="Email"
                        withAsterisk
                        placeholder="name@mail.com"
                        value={form.values.email}
                        onChange={(event) => form.setFieldValue('email', event.currentTarget.value)}
                        error={form.errors.email}
                        radius="md"
                    />
                    <Popover opened={popoverOpened} position="bottom" width="target" transitionProps={{ transition: 'pop' }}>
                        <Popover.Target>
                            <div
                                onFocusCapture={() => setPopoverOpened(true)}
                                onBlurCapture={() => setPopoverOpened(false)}
                            >
                                <PasswordInput
                                    label="Password"
                                    withAsterisk
                                    placeholder="Your password"
                                    value={form.values.password}
                                    onChange={(event) => {
                                        form.setFieldValue('password', event.currentTarget.value);
                                    }}
                                    error={form.errors.password}
                                    radius="md"
                                />
                            </div>
                        </Popover.Target>
                        <Popover.Dropdown>
                            <Progress color={color} value={strength} size={5} mb="xs" />
                            <PasswordRequirement label="Includes at least 8 characters" meets={form.values.password.length >= 8} />
                            {checks}
                        </Popover.Dropdown>
                    </Popover>
                    <PasswordInput
                        label="Confirm Password"
                        withAsterisk
                        placeholder="Confirm your password"
                        value={form.values.confirmPassword}
                        onChange={(event) => form.setFieldValue('confirmPassword', event.currentTarget.value)}
                        error={form.errors.confirmPassword}
                        radius="md"
                    />
                    <Checkbox mt={'sm'}
                        label={
                            <>
                                I accept the{' '}
                                <Anchor href="https://sortmyshoebox.com/terms-and-conditions" target="_blank" size="sm">
                                    Terms of Service
                                </Anchor>{' '}
                                and{' '}
                                <Anchor href="https://dev.sortmyshoebox.com/privacy-policy" target="_blank" size="sm">
                                    Privacy Policy
                                </Anchor>
                            </>
                        }
                        checked={form.values.terms}
                        onChange={(event) => form.setFieldValue('terms', event.currentTarget.checked)}
                        error={form.errors.terms}
                    />
                </Stack>
                <Group justify="space-between" mt="xl">
                    <Stack gap={4}>
                        <Text size="md">
                            Aready have an account?{' '}
                            <Anchor
                                component="button"
                                type="button"
                                c="blue"
                                size="md"
                                onClick={onLoginClick}
                            >
                                Sign In
                            </Anchor>
                        </Text>
                    </Stack>
                    <Button type="submit" radius="xl" loading={submitting}>
                        Sign Up
                    </Button>
                </Group>
            </form>
        </>
    );
};

export default SignUp;